/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AuthRequestService } from './auth-request.service';
import { ApiService } from '@api/services/api/api.service';
import {
  mergeMap,
  map,
  concatMap,
  ignoreElements,
  endWith
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, from } from 'rxjs';
import { UserDataDTO, AuthenticateDTO, UserAttributesDTO } from '@api/models';
import { HttpResponse } from '@angular/common/http';
import { AppService } from '@services/app/app.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * sessione utente
   */
  private authUser$ = new BehaviorSubject<UserDataDTO>(null);

  /**
   * lista di azioni che vengono eseguite dopo la logon
   */
  private afterLogonActions: Observable<void>[] = [];

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private authRequestService: AuthRequestService
  ) {}

  /**
   * effettua login
   */
  login(): Observable<boolean> {
    return this.authenticate(this.createAuthenticateData()).pipe(
      mergeMap(() => this.loadAuthUser())
    );
  }

  /**
   * effettua login tramite auth code
   */
  loginWithAuthCode(code: string): Observable<boolean> {
    return this.authenticate(this.createAuthenticateData(code)).pipe(
      mergeMap(() => this.loadAuthUser())
    );
  }

  /**
   * avvia sessione recuperando i dati dell'utente
   */
  loadAuthUser(): Observable<boolean> {
    return this.getUserData().pipe(
      map(res => this.setAuthUser(res)),
      mergeMap(() => this.runAfterLogonActions()),
      map(() => true)
    );
  }

  /**
   * aggiunge azione da eseguire dopo il logon
   */
  addActionAfterLogon(
    action: Observable<void> | (() => void) | (() => Observable<void>)
  ): void {
    if (action instanceof Observable) {
      this.addActionAsObservable(action);
    } else if (typeof action === 'function') {
      this.addActionAsCallback(action);
    }
  }

  /**
   * restituisce gli attributi dell'user autenticato
   */
  getAuthUserAttributes(): UserAttributesDTO {
    return this.getAuthUser().attributes || {};
  }

  /**
   * restituisce la sessione attuale
   */
  getAuthUser(): UserDataDTO {
    const authUser = this.authUser$.getValue();

    return (authUser && { ...authUser }) || {};
  }

  /**
   * restituisce osserbabile per aggiornamenti stato user autenticato
   */
  getAuthUserWhenChange(): Observable<UserDataDTO> {
    return this.authUser$.asObservable();
  }

  /**
   * determina se la sessione è impostata correttamente
   */
  isAuthenticated(): boolean {
    return !!this.isValidUser(this.authUser$.getValue());
  }

  /**
   * verifica la validità dell'auth code
   */
  private authenticate(data: AuthenticateDTO): Observable<boolean> {
    return this.apiService
      .postSecurityLogon({
        body: data,
        options: {
          observe: 'response',
          responseType: 'text'
        }
      })
      .pipe(
        map((res: HttpResponse<void>) => {
          this.authRequestService.saveAuthHeaderFromHeadersWhenNecessary(
            res && res.headers
          );
        }),
        map(() => true)
      );
  }

  /**
   * restituisce i dati dell'user autenticato
   */
  private getUserData(): Observable<UserDataDTO> {
    return this.apiService
      .getSecurityUserData({})
      .pipe(map(res => this.sanitizeUserData(res)));
  }

  /**
   * imposta i dati della sessione e controlla che sia valida
   */
  private setAuthUser(authUser: UserDataDTO): void {
    if (!this.isValidUser(authUser)) {
      throw new Error(`User's session is not valid`);
    }

    if (!!authUser) {
      this.authUser$.next(authUser);
    } else {
      this.authUser$.next(null);
    }
  }

  /**
   * controlla se la sessione è valida
   */
  private isValidUser(user: UserDataDTO): boolean {
    return !!user;
  }

  /**
   * aggiunge azione come osservabile
   */
  private addActionAsObservable(action: Observable<void>): void {
    this.afterLogonActions.push(action);
  }

  /**
   * aggiunge azione funzioen di callback
   */
  private addActionAsCallback(
    action: (() => void) | (() => Observable<void>)
  ): void {
    this.afterLogonActions.push(
      of(true).pipe(
        mergeMap(() => {
          const output = action();

          if (output instanceof Observable) {
            return output;
          } else {
            return of(output);
          }
        })
      )
    );
  }

  /**
   * esegue le operazioni registrate da effettuare dopo il logon
   */
  private runAfterLogonActions(): Observable<boolean> {
    if (this.afterLogonActions.length === 0) {
      return of(true);
    }

    return from(this.afterLogonActions).pipe(
      concatMap(x => x),
      ignoreElements(),
      endWith(true)
    );
  }

  /**
   * crea l'oggetto per eseguire l'autenticazione
   */
  private createAuthenticateData(code?: string): AuthenticateDTO {
    const sender = this.getAuthSender();
    const testAttr = this.getTestAttr();

    const data: AuthenticateDTO = {
      'application-code': this.getAuthAppCode()
    };

    if (!!sender && !this.appService.getConfig().security.hideSender) {
      data.sender = sender;
    }

    if (!!testAttr) {
      data['test-attribute'] = testAttr;
    }

    if (!!code) {
      data['uuid-info'] = code;
    }

    return data;
  }

  /**
   * restituisce il sender per eseguire l'autenticazione
   */
  private getAuthSender(): string {
    return this.appService.getAppName('container');
  }

  /**
   * restituisce il codice application per eseguire l'autenticazione
   */
  private getAuthAppCode(): string {
    return this.appService.getEnvironment().appCode.toUpperCase();
  }

  /**
   * restituisce il valore dell'item test attribute
   */
  private getTestAttr(): string {
    return localStorage.getItem('test-attribute');
  }

  /**
   * controlla e sanifica i dati dalla userdata
   */
  private sanitizeUserData(data: UserDataDTO): UserDataDTO {
    // mantengo compatibilità con il parametro deprecato 'position-id'
    if (
      data &&
      data.attributes &&
      typeof data.attributes['position-id'] === 'string' &&
      !data.attributes.positionId
    ) {
      data.attributes.positionId = data.attributes['position-id'].trim();
    }

    return data;
  }
}
